import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmDealerCustomerDetailsQuery,
  useWsmDealerCustomerUnassignMutation,
  useWsmDealerCustomerUpdateMutation,
  useWsmDealerGroupCodesQuery,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useState } from "react";
import { useIntl } from "react-intl";

import WsmDealerCustomerDetailsPage from "../components/WsmDealerCustomerDetailsPage/WsmDealerCustomerDetailsPage";
import { sectionMessages } from "../messages";
import { wsmDealerCustomerListPath } from "../urls";
import { customerToFormData, getCustomerUpdateInput, getGroupLabel } from "../utils";

const GROUP_CHOICES = 100;

export const WsmDealerCustomerDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [unassignOpen, setUnassignOpen] = useState(false);
  const { data, loading } = useWsmDealerCustomerDetailsQuery({ variables: { id } });
  const { data: groupData } = useWsmDealerGroupCodesQuery({ variables: { first: GROUP_CHOICES } });
  const [updateCustomer, updateCustomerOpts] = useWsmDealerCustomerUpdateMutation({
    onCompleted: result => {
      if (!result.wsmDealerCustomerUpdate?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
      }
    },
  });
  const [unassign, unassignOpts] = useWsmDealerCustomerUnassignMutation({
    onCompleted: result => {
      if (!result.wsmDealerCustomerUnassign?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmDealerCustomerListPath);
      }
    },
  });
  const customer = data?.wsmDealerCustomer;

  if (loading || !customer) {
    return (
      <DetailPageLayout>
        <TopNav
          href={wsmDealerCustomerListPath}
          title={intl.formatMessage(sectionMessages.customerListTitle)}
        />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  const errors = updateCustomerOpts.data?.wsmDealerCustomerUpdate?.errors ?? [];
  const formData = customerToFormData(customer);

  return (
    <>
      <WsmDealerCustomerDetailsPage
        key={customer.id}
        title={formData.userLabel}
        initialData={formData}
        groupOptions={(mapEdgesToItems(groupData?.wsmDealerGroups) ?? []).map(group => ({
          value: group.id,
          label: getGroupLabel(group),
        }))}
        errors={errors}
        disabled={updateCustomerOpts.loading}
        saveButtonBarState={getMutationState(
          updateCustomerOpts.called,
          updateCustomerOpts.loading,
          errors,
        )}
        onSubmit={values =>
          updateCustomer({ variables: { id: customer.id, input: getCustomerUpdateInput(values) } })
        }
        onUnassign={() => setUnassignOpen(true)}
      />
      <ActionDialog
        open={unassignOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.unassignTitle)}
        confirmButtonState={getMutationState(unassignOpts.called, unassignOpts.loading, [])}
        onClose={() => setUnassignOpen(false)}
        onConfirm={() => unassign({ variables: { id: customer.id } })}
      >
        {intl.formatMessage(sectionMessages.unassignContent, { name: formData.userLabel })}
      </ActionDialog>
    </>
  );
};

export default WsmDealerCustomerDetailsView;
